import { useState, useEffect } from 'react';
import { Alert, LinearProgress, Box, IconButton, Chip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import SignalWifi1BarIcon from '@mui/icons-material/SignalWifi1Bar';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

const ConnectionStatusSnackbar = () => {
    const [open, setOpen] = useState(false);
    const [seconds, setSeconds] = useState(30);
    const { isOnline, isSlowNetwork, networkSpeed, checkConnection } = useNetworkStatus();

    useEffect(() => {
        console.log('ConnectionStatusSnackbar - Network status changed:', { isOnline, isSlowNetwork });
        if (!isOnline || isSlowNetwork) {
            setOpen(true);
            setSeconds(30);
        } else {
            setOpen(false);
            setSeconds(0);
        }
    }, [isOnline, isSlowNetwork]);

    useEffect(() => {
        if (!open) return;

        const timer = setInterval(() => {
            setSeconds(prev => {
                if (prev <= 1) {
                    checkConnection();
                    return 30;
                }
                return prev - 1;
            });
        }, 1000);

        // eslint-disable-next-line consistent-return
        return () => clearInterval(timer);

    }, [open, checkConnection]);

    const handleRetry = () => {
        setSeconds(30);
        checkConnection();
    };

    if (!open) return null;

    // Determine message and severity based on network status
    const getNetworkStatus = () => {
        if (!isOnline) {
            return {
                message: 'Internet unavailable',
                severity: 'error',
                icon: <SignalWifiOffIcon fontSize="small" />
            };
        }
        if (isSlowNetwork) {
            return {
                message: 'Slow network detected',
                severity: 'warning',
                icon: <SignalWifi1BarIcon fontSize="small" />
            };
        }
        return null;
    };

    const networkStatus = getNetworkStatus();
    if (!networkStatus) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                zIndex: 1400,
                boxShadow: 1,
                left: { xs: 0, sm: 0 },
                bottom: { xs: 'auto', sm: 0 },
                top: { xs: 0, sm: 'auto' },
                right: { xs: 0, sm: 'auto' },
                width: { xs: '100%', sm: 'auto' },
            }}
        >
            <LinearProgress
                color={networkStatus.severity === 'error' ? 'primary' : 'warning'}
                sx={{
                    width: '100%',
                    height: '4px',
                }}
            />
            <Alert
                severity={networkStatus.severity}
                icon={networkStatus.icon}
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    borderRadius: 0,
                    padding: '0px 16px',
                    minHeight: '40px',
                    '& .MuiAlert-message': {
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }
                }}
            >
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{networkStatus.message}. Retrying in ({seconds}s)</span>
                    {networkSpeed && isSlowNetwork && (
                        <Chip
                            label={`${Math.round(networkSpeed)}ms`}
                            size="small"
                            color="warning"
                            variant="outlined"
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={handleRetry}
                        sx={{
                            color: 'inherit',
                            padding: '4px'
                        }}
                    >
                        <RefreshIcon color="primary" fontSize="small" />
                    </IconButton>
                </Box>
            </Alert>
        </Box>
    );
};

export default ConnectionStatusSnackbar;